import { DeadlineCountdown } from "@/components/deadline-countdown";

import {
  ArrowRight,
  CalendarClock,
  ChevronDown,
  Eye,
  EyeOff,
  GraduationCap,
  Lock,
} from "lucide-react";
import { useState } from "react";

import { AsyncBoundary, EmptyState, LoadingRows } from "@/components/data-state";
import { TeamNotice } from "@/components/team-notice";
import { Badge } from "@/components/ui/badge";
import { ButtonLink } from "@/components/button-link";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useAsync } from "@/hooks/use-async";
import { useApiKey } from "@/hooks/use-auth";
import type { Challenge, CourseWithTerm } from "@/lib/api";
import { publicApi, studentApi } from "@/lib/api";
import {
  PAST_LABEL,
  type PastReason,
  currentTermStartOf,
  pastReason,
} from "@/lib/course-status";
import { isDeadlinePassed, notOpenYet } from "@/lib/format";
import { OpensCountdown } from "@/components/opens-countdown";
import { cn } from "@/lib/utils";

const TYPE_LABEL: Record<string, string> = {
  competition: "Graded",
  development: "Practice",
};

function ChallengeCard({ challenge }: { challenge: Challenge }) {
  const closed = isDeadlinePassed(challenge.deadline);

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-base leading-snug">
            {challenge.name}
          </CardTitle>
          <span className="flex shrink-0 items-center gap-1.5">
            {challenge.visibility === "enrolled" ? (
              <Badge variant="outline" className="gap-1">
                <Lock className="size-3" aria-hidden />
                Enrolled
              </Badge>
            ) : null}
            <Badge
              variant={challenge.type === "competition" ? "default" : "secondary"}
            >
              {TYPE_LABEL[challenge.type] ?? challenge.type}
            </Badge>
          </span>
        </div>
        {challenge.description ? (
          <CardDescription className="line-clamp-3">
            {challenge.description}
          </CardDescription>
        ) : null}
      </CardHeader>

      <CardContent className="text-muted-foreground flex-1 space-y-2 text-sm">
        {challenge.opens_at !== null && notOpenYet(challenge) ? (
          <OpensCountdown opensAt={challenge.opens_at} variant="card" />
        ) : null}
        <p
          className={cn(
            "flex items-center gap-1.5",
            closed && "text-destructive",
          )}
        >
          <CalendarClock className="size-4" aria-hidden />
          <DeadlineCountdown deadline={challenge.deadline} />
        </p>
        {challenge.collects_report ? (
          <p className="flex items-center gap-1.5">
            <CalendarClock className="size-4" aria-hidden />
            <span>Report: <DeadlineCountdown deadline={challenge.report_deadline} kind="report" /></span>
          </p>
        ) : null}
        <p className="flex items-center gap-1.5">
          {challenge.show_trajectory ? (
            <>
              <Eye className="size-4" aria-hidden />
              Trajectory visible
            </>
          ) : (
            <>
              <EyeOff className="size-4" aria-hidden />
              Trajectory hidden
            </>
          )}
        </p>
      </CardContent>

      <CardFooter>
        <ButtonLink to={`/challenges/${challenge.id}`} className="w-full">
          Open
          <ArrowRight className="size-4" aria-hidden />
        </ButtonLink>
      </CardFooter>
    </Card>
  );
}

function CourseSection({
  course,
  challenges,
  past,
  defaultOpen,
}: {
  course: CourseWithTerm | undefined;
  challenges: Challenge[];
  past: PastReason | null;
  defaultOpen: boolean;
}) {
  // Default follows the data (which can change once /courses resolves); once the
  // reader toggles a section their choice wins.
  const [override, setOverride] = useState<boolean | null>(null);
  const open = override ?? defaultOpen;
  const count = challenges.length;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOverride}
      render={<section />}
      className="space-y-4"
    >
      <CollapsibleTrigger className="group/course hover:bg-muted/50 -mx-2 flex w-[calc(100%+1rem)] flex-wrap items-baseline gap-x-3 gap-y-1 rounded-md border-b px-2 pb-2 pt-1 text-left transition-colors">
        <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
          <GraduationCap className="size-4" aria-hidden />
          {course?.code ?? "Other"}
        </h2>
        {course ? (
          <span className="text-muted-foreground text-sm">
            {course.title ? `${course.title} · ` : ""}
            {course.term.label}
          </span>
        ) : null}
        {past ? <Badge variant="secondary">{PAST_LABEL[past]}</Badge> : null}
        <span className="text-muted-foreground ml-auto flex items-center gap-1.5 self-center text-xs">
          {open ? null : `${count} challenge${count === 1 ? "" : "s"}`}
          <ChevronDown
            className="size-4 transition-transform group-data-[panel-open]/course:rotate-180"
            aria-hidden
          />
        </span>
      </CollapsibleTrigger>

      <CollapsibleContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {challenges.map((challenge) => (
          <ChallengeCard key={challenge.id} challenge={challenge} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function HomeRoute() {
  const apiKey = useApiKey();

  // The key is passed but never required: an anonymous visitor sees every public
  // challenge, and a signed-in student additionally sees their own course's,
  // whatever its visibility.
  const challenges = useAsync(
    () => publicApi.challenges(),
    [apiKey],
  );
  const courses = useAsync(() => publicApi.courses(), []);
  // Only for the team notice: a student with no team should hear about it here, on
  // the page they land on, not only on the team page they have no reason to open.
  const me = useAsync(apiKey ? () => studentApi.me(apiKey) : null, [apiKey]);

  const courseById = new Map((courses.data ?? []).map((c) => [c.id, c]));
  // Read once per mount: the compiler lint rejects Date.now() in render, and "now" does not
  // need to be fresher than the page.
  const [now] = useState(() => Date.now());
  const currentTermStart = currentTermStartOf(courses.data ?? [], now);

  return (
    <div className="space-y-10">
      <h1 className="text-2xl font-semibold tracking-tight">Challenges</h1>

      {me.data ? <TeamNotice me={me.data} where="home" /> : null}

      <AsyncBoundary
        state={challenges}
        loading={<LoadingRows rows={2} />}
        empty={<EmptyState title="No open challenges" />}
      >
        {(list) => {
          // Grouped by course, ordered by term (newest first) then course code.
          // The term used to exist only as a prefix inside each challenge name, so
          // this grouping meant parsing "ROB-UY 3203 (Spring 26) - ..." strings.
          const groups = new Map<string, Challenge[]>();
          for (const challenge of list) {
            const bucket = groups.get(challenge.course_id) ?? [];
            bucket.push(challenge);
            groups.set(challenge.course_id, bucket);
          }

          const sections = [...groups.entries()].map(([courseId, items]) => {
            const course = courseById.get(courseId);
            return {
              courseId,
              course,
              items,
              past: pastReason(course, items, currentTermStart),
            };
          });

          sections.sort((a, b) => {
            // Unknown courses last: /courses may not have resolved yet, and a
            // challenge whose course is missing should not vanish from the page.
            if (!a.course || !b.course) return a.course ? -1 : b.course ? 1 : 0;
            return (
              b.course.term.starts_at - a.course.term.starts_at ||
              // Within a term, courses still running sit above finished ones.
              Number(a.past !== null) - Number(b.past !== null) ||
              a.course.code.localeCompare(b.course.code)
            );
          });

          // Past sections start collapsed. If that would fold everything (say,
          // between terms), keep the newest one open so the page is not empty.
          const anyCurrent = sections.some((s) => s.past === null);

          return (
            <div className="space-y-10">
              {sections.map((s, i) => (
                <CourseSection
                  key={s.courseId}
                  course={s.course}
                  challenges={s.items}
                  past={s.past}
                  defaultOpen={s.past === null || (!anyCurrent && i === 0)}
                />
              ))}
            </div>
          );
        }}
      </AsyncBoundary>
    </div>
  );
}
